import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import type {
  WebhookDelivery,
  WebhookDeliveryStatus,
  WebhookEndpoint,
} from '../../lib/api/operatorConsole';

interface Props {
  deliveries: WebhookDelivery[];
  endpoints: WebhookEndpoint[];
  loading: boolean;
  busy: boolean;
  status: WebhookDeliveryStatus | '';
  onStatusChange: (status: WebhookDeliveryStatus | '') => void;
  onResend: (delivery: WebhookDelivery) => void;
}

const STATUSES: WebhookDeliveryStatus[] = [
  'pending',
  'succeeded',
  'failed',
  'exhausted',
  'skipped',
];

/** What the operator most wants to know at a glance: did it land, and if not,
 *  what came back. */
function outcome(delivery: WebhookDelivery): string {
  if (delivery.status === 'succeeded') {
    return `HTTP ${delivery.last_response_status ?? '2xx'}`;
  }
  if (delivery.last_error) return delivery.last_error;
  if (delivery.status === 'pending') return 'waiting to be sent';
  return '—';
}

export function DeliveriesTable({
  deliveries,
  endpoints,
  loading,
  busy,
  status,
  onStatusChange,
  onResend,
}: Props) {
  const [expanded, setExpanded] = useState<number | null>(null);

  const urlById = new Map(endpoints.map((endpoint) => [endpoint.id, endpoint.url]));

  return (
    <div className="max-w-4xl">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="text-[14px] font-semibold text-[var(--co-text)]">Recent deliveries</h2>
        <select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value as WebhookDeliveryStatus | '')}
          className="rounded-md border border-[var(--co-border)] bg-[var(--co-surface)] px-2 py-1 text-[12px] text-[var(--co-text)]"
        >
          <option value="">All statuses</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
        {loading ? (
          <p className="p-4 text-[12px] text-[var(--co-muted)]">Loading…</p>
        ) : deliveries.length === 0 ? (
          <p className="p-4 text-[12px] text-[var(--co-muted)]">
            Nothing sent yet. Add an endpoint, subscribe it to an event, and use Send test to check
            it answers.
          </p>
        ) : (
          <ul>
            {deliveries.map((delivery) => (
              <li
                key={delivery.id}
                className="border-b border-[var(--co-border)] px-4 py-3 last:border-b-0"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[13px] text-[var(--co-text)]">
                      <code>{delivery.event_type}</code>
                      <span className="text-[var(--co-muted)]">{delivery.status}</span>
                      <span className="text-[11px] text-[var(--co-muted)]">
                        attempt {delivery.attempts}/{delivery.max_attempts}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-[12px] text-[var(--co-muted)]">
                      {urlById.get(delivery.endpoint_id) ?? 'deleted endpoint'}
                    </div>
                    <div className="mt-1 text-[11px] text-[var(--co-muted)]">
                      {new Date(delivery.created_at).toLocaleString()} · {outcome(delivery)}
                    </div>
                  </div>

                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => setExpanded(expanded === delivery.id ? null : delivery.id)}
                    >
                      {expanded === delivery.id ? 'Hide body' : 'View body'}
                    </Button>
                    {/* Always available: a notification moves no money, so
                        replaying one is safe even after it arrived. */}
                    <Button variant="ghost" disabled={busy} onClick={() => onResend(delivery)}>
                      Resend
                    </Button>
                  </div>
                </div>

                {expanded === delivery.id && (
                  <pre className="mt-3 overflow-x-auto rounded-lg bg-[var(--co-bg)] p-3 text-[11px] text-[var(--co-text)]">
                    {JSON.stringify(delivery.payload, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
